import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const initial = {
  full_name: "", company_rut: "", address: "", phone: "",
  email: "", company_name: "", website: "", message: "",
};

export default function QuoteDialog({ open, onOpenChange, product }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        ...form,
        product_id: product?.id || null,
        product_name: product?.name || "",
      });
      toast.success("Solicitud enviada", {
        description: "Nos pondremos en contacto a la brevedad.",
      });
      setForm(initial);
      onOpenChange(false);
    } catch (err) {
      toast.error("Error al enviar", {
        description: err?.response?.data?.detail || "Intenta nuevamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = "block text-xs font-display font-bold text-slate-700 mb-1.5 uppercase tracking-wider";
  const inputCls = "w-full border border-slate-300 rounded-none px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-slate-900 transition-shadow";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none border-2 border-slate-950 max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-600 mb-1">Formulario de cotización</div>
          <DialogTitle className="font-display text-2xl md:text-3xl uppercase tracking-tight">
            Solicita tu cotización
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {product ? <>Producto: <span className="font-semibold text-slate-950">{product.name}</span></>
                     : "Completa el formulario y nuestro equipo se pondrá en contacto."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2" data-testid="quote-form">
          <div>
            <label className={labelCls}>Nombre completo *</label>
            <input required name="full_name" value={form.full_name} onChange={handleChange} className={inputCls} data-testid="quote-input-full-name" />
          </div>
          <div>
            <label className={labelCls}>Nombre empresa *</label>
            <input required name="company_name" value={form.company_name} onChange={handleChange} className={inputCls} data-testid="quote-input-company-name" />
          </div>
          <div>
            <label className={labelCls}>RUT empresa *</label>
            <input required name="company_rut" value={form.company_rut} onChange={handleChange} className={inputCls} placeholder="76.123.456-7" data-testid="quote-input-company-rut" />
          </div>
          <div>
            <label className={labelCls}>Teléfono *</label>
            <input required name="phone" value={form.phone} onChange={handleChange} className={inputCls} placeholder="+56 9 ..." data-testid="quote-input-phone" />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input required type="email" name="email" value={form.email} onChange={handleChange} className={inputCls} data-testid="quote-input-email" />
          </div>
          <div>
            <label className={labelCls}>Página web</label>
            <input name="website" value={form.website} onChange={handleChange} className={inputCls} placeholder="https://..." data-testid="quote-input-website" />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Dirección *</label>
            <input required name="address" value={form.address} onChange={handleChange} className={inputCls} data-testid="quote-input-address" />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Mensaje (opcional)</label>
            <textarea name="message" value={form.message} onChange={handleChange} rows={3} className={inputCls} data-testid="quote-input-message" />
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-3 font-display font-bold uppercase text-xs tracking-wider text-slate-600 hover:text-slate-950" data-testid="quote-cancel-btn">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-3 text-sm btn-brutal disabled:opacity-60 flex items-center gap-2" data-testid="quote-submit-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Enviar solicitud
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
